/**
 * web/src/lib/referrals.ts — Pear referrals (Share Pear), client half.
 *
 * The worker owns the ledger (worker/src/api/referrals.ts, card 3MWZ8Zs7jA7e);
 * this module owns the three things the web app does with it:
 *
 *   capture — `?ref=<code>` is READ at boot and kept in `pear.ref`. The query
 *             string itself is never rewritten: other code reads its own
 *             params off it (?pair_code, ?invite, ?oauth_code, ?reset_token…),
 *             so this one only ever reads. A best-effort GET
 *             /api/referrals/track?code=… sets the worker's 60-day cookie
 *             beside the localStorage copy; failures are swallowed, because
 *             attribution must never block or break a boot.
 *   apply   — once a couple token exists, the stored code is redeemed with
 *             POST /api/referrals/apply. Both sides earn 30 days of Pear Plus
 *             (card 3MWZ8Zs7jA7e, "same as JitsLab — get 30 additional days"):
 *             the friend's days land the moment they redeem, the referrer's
 *             vest when the friend's couple forms (second partner joins).
 *             Every refusal — own link (400), already used (409), unknown
 *             (404), unconfigured (503) — is quiet: sign-up and pairing must
 *             never be error-toasted by a dead referral link.
 *   link    — GET /api/referrals/link, the couple's own code + url + the
 *             server's own share sentence + how many friends have vested.
 *
 * localStorage keys (dotted on purpose, beside the app's snake keys):
 *   pear.ref          the captured code, waiting to be redeemed
 *   pear.ref.applied  the code this couple has already redeemed (or that was
 *                     refused for good) — the guard that stops a repeat POST
 */
import { api, getToken } from './api';
import { REFERRAL_COOKIE_DAYS } from '../../../shared/billing';

const LS_REF = 'pear.ref';
const LS_REF_APPLIED = 'pear.ref.applied';

/** Fired on window after a successful apply — ReferralToast names the reward. */
export const REFERRAL_APPLIED_EVENT = 'pear:referral-applied';

/** GET /api/referrals/link — the couple's own link, minted on first ask. */
export interface ReferralLink {
  code: string;
  url: string;
  /** The server's share sentence, so the share sheet and the card agree. */
  shareText: string;
  rewardDays: number;
  /** Friends whose couples have FORMED — days already vested, never a promise. */
  referred: number;
  /** True when this couple's card hands out the FOUNDER code (card
   *  NThh9EjYzupt) — Plus until `plusUntil`, not 30 days each side. */
  founder?: boolean;
  /** The founder grant's absolute end (ms epoch); null/absent otherwise. */
  plusUntil?: number | null;
}

/** POST /api/referrals/apply — what the worker answers on success. */
export interface ReferralApplyResult {
  ok: true;
  rewardDays: number;
  /** True when the code redeemed was the founder code — its reward is Plus
   *  until `plusUntil`, not `rewardDays` of Plus. */
  founder?: boolean;
  plusUntil?: number | null;
  /** True once the referrer's days were paid (the friend's couple is whole). */
  referrerPaid: boolean;
  entitlement?: unknown;
}

/**
 * The four refusals the worker documents for apply — each means this link is
 * dead FOR THIS COUPLE, so the stored code is dropped and never retried.
 * Anything else (offline, cold worker) is kept for the next launch.
 */
const REFUSAL_CODES = new Set([
  'referral_self', // 400 — you cannot use your own link
  'referral_already', // 409 — this couple has already used a referral link
  'referral_unknown', // 404 — unknown referral link
  /* `referrals_unconfigured` (503) is deliberately NOT here (pre-deploy
     review, 2026-10-17): a worker without its D1 binding, or a deploy in
     flight, is a TEMPORARY server state, and dropping the capture for it
     throws away a legitimate referral over an outage. It is kept for the next
     launch, like any other unreachable-worker failure. */

]);

/** The stored capture and its age. `at` 0 means "no local timestamp" — a
 *  legacy plain-string capture or the server's own cookie, which carries its
 *  own 60-day Max-Age, so only a locally stamped capture expires here. */
interface Capture {
  code: string;
  at: number;
}

const CAPTURE_TTL_MS = REFERRAL_COOKIE_DAYS * 86_400_000;

/** The /track request this device has in flight, if any — clearCapture waits
 *  for it so a late Set-Cookie cannot undo a clear (review 4). `unknown`, not
 *  `Response`: a rejected fetch must be able to settle here too (review 5 —
 *  a null-body 204 cannot be built with a body, and the cleanup chain must
 *  never be the thing that throws). */
let trackInFlight: Promise<unknown> | null = null;

/** localStorage first, the worker's cookie as the fallback. The cookie exists
 *  for the device that never carried ?ref= into the sign-up screen (or whose
 *  storage was cleared); the two are written by the same capture, so they
 *  cannot disagree about anything but age. */
function readCapture(): Capture | null {
  try {
    const raw = localStorage.getItem(LS_REF);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Capture;
        if (parsed && typeof parsed.code === 'string' && parsed.code) {
          return { code: parsed.code, at: Number(parsed.at) || 0 };
        }
      } catch {
        // Legacy plain-string capture (pre-2026-10-17): usable, undated.
        const code = raw.trim();
        if (code) return { code, at: 0 };
      }
    }
  } catch {
    /* storage blocked — fall through to the cookie */
  }
  try {
    const hit = document.cookie.match(/(?:^|;\s*)pear_ref=([^;]+)/);
    if (hit) return { code: decodeURIComponent(hit[1]).trim(), at: 0 };
  } catch {
    /* no cookie access */
  }
  return null;
}

function clearCookie(): void {
  /* The worker's own capture cookie must go too (pre-deploy review,
     2026-10-17): clearing only the local copy left pear_ref readable for up
     to 60 days, so a DIFFERENT couple signing in on this browser could still
     redeem a link that had already been spent or refused. Expired the same
     way a browser expires it — the path and SameSite must match the write
     or the clear lands on a different cookie. */
  try {
    document.cookie = 'pear_ref=; Path=/; Max-Age=0; SameSite=Lax';
  } catch {
    /* no cookie access */
  }
}

function clearCapture(): void {
  try {
    localStorage.removeItem(LS_REF);
  } catch {
    /* storage blocked */
  }
  clearCookie();
  /* The clear has to outlast an in-flight /track (pre-deploy review 4): that
     response carries its own Set-Cookie, and a delayed one landing AFTER this
     line would put pear_ref back — attribution for a link that has already
     been spent or refused, readable by the next couple on this browser. The
     fetch resolves after its headers are applied, so re-clearing when it
     settles is exactly late enough. */
  if (trackInFlight) void trackInFlight.then(() => clearCookie(), () => clearCookie());
}

/** The code this device is holding for a friend's couple, or null. Expired
 *  captures are dropped here rather than redeemed: the link's promise is
 *  60 days old at that point (the same window the worker's cookie carries). */
export function pendingReferralCode(): string | null {
  const cap = readCapture();
  if (!cap) return null;
  if (cap.at > 0 && Date.now() - cap.at > CAPTURE_TTL_MS) {
    clearCapture();
    return null;
  }
  return cap.code;
}

/**
 * Boot capture: read `?ref=` and keep it. READ-ONLY on the URL — the query
 * string is never rewritten, because every other surface reads its own params
 * off it. Newest link wins; a repeat tap of an already-redeemed link is
 * caught by the applied marker in applyPendingReferral(), which drops it
 * without a POST.
 */
export function captureReferralFromUrl(): void {
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(window.location.search).get('ref');
  } catch {
    return; // unreadable URL — nothing to capture
  }
  const code = raw?.trim();
  if (!code) return;
  try {
    localStorage.setItem(LS_REF, JSON.stringify({ code, at: Date.now() }));
  } catch {
    /* storage blocked: the cookie below is the only capture this device gets */
  }
  // Best-effort attribution only — the worker sets its 60-day pear_ref cookie
  // here. No token (a friend opens this BEFORE sign-up), failures swallowed.
  // Held while it runs (review 4): clearCapture must be able to expire the
  // cookie this response is about to set.
  const track: Promise<unknown> = fetch(`/api/referrals/track?code=${encodeURIComponent(code)}`, {
    credentials: 'include',
  }).catch(() => undefined); // failures settle quietly — attribution never surfaces an error
  trackInFlight = track;
  void track.then(
    () => {
      if (trackInFlight === track) trackInFlight = null;
    },
    () => {
      if (trackInFlight === track) trackInFlight = null;
    },
  );
}

/** Read the {error:{code}} out of an api() rejection — null when unparseable. */
function refusalCodeOf(e: unknown): string | null {
  const raw = e instanceof Error ? e.message : '';
  if (!raw.startsWith('{')) return null; // network failure, not an API answer
  try {
    const code = (JSON.parse(raw) as { error?: { code?: string } })?.error?.code;
    return typeof code === 'string' && code ? code : null;
  } catch {
    return null;
  }
}

/**
 * Redeem the stored code — call once this device holds a couple token (the
 * app's own "signed in with a couple" condition). Never throws, never
 * error-toasts: true means the reward landed (the event is already fired),
 * false means nothing happened (no code, already applied, refused, or the
 * worker could not be reached — in which case the code is kept for the next
 * launch, and the refusal codes above are the only ones that drop it).
 */
export async function applyPendingReferral(): Promise<boolean> {
  const code = pendingReferralCode();
  if (!code) return false;
  if (!getToken()) return false; // no couple token yet — wait for the session
  /* The applied marker is keyed by the COUPLE, not the device (pre-deploy
     review, 2026-10-17): signing out and forming a different couple on the
     same phone must not inherit the previous couple's redemption. */
  let coupleId = '';
  try {
    coupleId = localStorage.getItem('pear_couple_id') ?? '';
  } catch {
    /* storage blocked — the marker degrades to code-only */
  }
  const marker = JSON.stringify({ code, coupleId });
  try {
    const stored = localStorage.getItem(LS_REF_APPLIED);
    if (stored === marker || (coupleId === '' && stored === JSON.stringify({ code, coupleId: '' }))) {
      // This couple already redeemed (or was refused for good on) this exact
      // link — a repeat tap must not re-POST. Drop the stale capture quietly.
      clearCapture();
      return false;
    }
  } catch {
    return false; // storage blocked — nothing to guard, nothing to redeem
  }
  try {
    const r = await api<ReferralApplyResult>('/api/referrals/apply', { body: { code } });
    try {
      clearCapture();
      localStorage.setItem(LS_REF_APPLIED, marker);
    } catch {
      /* storage blocked: the reward already landed server-side */
    }
    window.dispatchEvent(
      new CustomEvent(REFERRAL_APPLIED_EVENT, {
        detail: { rewardDays: r.rewardDays, founder: Boolean(r.founder) },
      }),
    );
    return true;
  } catch (e) {
    const refusal = refusalCodeOf(e);
    if (refusal && REFUSAL_CODES.has(refusal)) {
      // Dead for this couple: forget it so it never fires again, and stay
      // silent — a bad link must not look like a broken sign-up.
      try {
        clearCapture();
        localStorage.setItem(LS_REF_APPLIED, marker);
      } catch {
        /* storage blocked — the POST is not retried this session anyway */
      }
      console.debug('[Pear] Referral not applied:', refusal);
      return false;
    }
    // Offline / cold worker: keep the code for the next launch. App.tsx's
    // one-attempt-per-launch ref stops it re-firing inside this session.
    console.debug('[Pear] Referral apply failed (kept for next launch):', e);
    return false;
  }
}

/** GET /api/referrals/link — the couple's own shareable link. */
export async function fetchReferralLink(): Promise<ReferralLink> {
  return api<ReferralLink>('/api/referrals/link');
}
